<script lang="ts">
    /* eslint svelte/no-at-html-tags: "off" */
    import { cx } from '$lib/utils/cx';
    import {
        MyNotificationsCountQueryDocument,
        Notification_DeleteNotificationMutationDocument,
        Notification_ToggleReadMutationDocument,
        type NotificationFragment,
    } from '$gql/graphql.js';
    import MediaImage from '$components/MediaImage/MediaImage.svelte';
    import { getContainer } from '$components/Context/ContainerContext';
    import MenuLink from '$components/ButtonMenu/MenuLink.svelte';
    import ButtonMenu from '$components/ButtonMenu/ButtonMenu.svelte';
    import { melt } from '@melt-ui/svelte';
    import { type MeltWithAction, meltWithActionDefault } from '$lib/utils/melt';
    import { documentName } from '$gql/documentName';

    let className: string = '';
    export { className as class };

    const { urlHelper, apollo, intl, svelteClient } = getContainer();

    export let meltItem: MeltWithAction = meltWithActionDefault;

    export let notification: NotificationFragment;
    export let withMenu: boolean = false;

    async function deleteNotification() {
        await apollo().mutate({
            mutation: Notification_DeleteNotificationMutationDocument,
            variables: {
                id: notification.id,
            },
            refetchQueries: [documentName(MyNotificationsCountQueryDocument)],
            awaitRefetchQueries: true,
        });

        await svelteClient.invalidate((url) => url.href.startsWith('app:notifications-'));
    }

    async function toggleRead() {
        await apollo().mutate({
            mutation: Notification_ToggleReadMutationDocument,
            variables: {
                id: notification.id,
            },
            refetchQueries: [documentName(MyNotificationsCountQueryDocument)],
            awaitRefetchQueries: true,
        });

        await svelteClient.invalidate(
            (url) =>
                url.href.startsWith('app:notifications-is-not-read') ||
                url.href.startsWith('app:notifications-is-read'),
        );
    }

    const actions = {
        'toggle-read': {
            type: 'async' as const,
            action: async () => {
                await toggleRead();
                return {
                    type: 'success' as const,
                };
            },
        },
        delete: {
            type: 'async' as const,
            action: async () => {
                await deleteNotification();
                return {
                    type: 'success' as const,
                };
            },
        },
    };
</script>

<div
    data-read={notification.readDate != null ? true : undefined}
    class={cx(
        className,
        'flex w-full',
        'border-solid border-0 border-b border-gray-350 last:border-0',
        'text-gray-800',
        'bg-white hover:bg-gray-200 focus-within:bg-gray-200',
        'data-[read]:bg-gray-100 data-[read]:hover:bg-gray-200 data-[read]:focus-within:bg-gray-200',
        'group',
        'outline-none',
    )}
>
    <a
        data-sveltekit-reload
        use:melt={meltItem}
        href={urlHelper().notificationLink(notification)}
        class="flex-1 hover:no-underline focus:no-underline flex py-[8px] px-[12px] gap-x-[8px] outline-none"
    >
        {#if notification.image != null}
            <MediaImage
                class="size-[40px] rounded-full object-cover shrink-0"
                alt="notification"
                mediaId={notification.image.id}
                format="w100"
            />
        {:else}
            <img
                src={notification.placeholderImage}
                class="force-w-[40px] force-h-[40px] rounded-full object-cover"
                alt="notification"
            />
        {/if}

        <div class="flex flex-col gap-y-[4px]">
            <div class={'text-gray-800 text-14/[20px]'}>
                {@html notification.text}
            </div>
            <div class="text-11/[120%] text-gray-500">
                {intl().formatDate(notification.date, 'PPPpp')}
            </div>
        </div>
    </a>
    <div class={'flex items-center'}>
        {#if withMenu}
            <ButtonMenu
                class={cx(
                    'btn-reset ml-2 ls:invisible group-hover:visible w-10 h-10 rounded-full shadow text-14 bg-white hover:bg-gray-100 flex items-center justify-center m-0',
                )}
                {actions}
            >
                <i slot="button" class="fa fa-ellipsis-v" aria-hidden={true}></i>
                <svelte:fragment slot="menu">
                    <MenuLink icon="fa fa-check" key="toggle-read">
                        {#if notification.readDate != null}
                            Marquer comme non lue
                        {:else}
                            Marquer comme lue
                        {/if}
                    </MenuLink>

                    <MenuLink icon="fa fa-trash" key="delete"
                        >Supprimer cette notification</MenuLink
                    >
                </svelte:fragment>
            </ButtonMenu>
        {/if}
        <button
            on:click|preventDefault={toggleRead}
            class={cx(
                'btn-reset',
                'px-6',
                'h-full',
                'text-sm',
                'hover:opacity-75',
                notification.readDate != null ? 'text-gray-600' : 'text-brand-blue',
            )}
        >
            <i class={'fa fa-circle'} />
        </button>
    </div>
</div>
